import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { FolderPicker } from '../components/FolderPicker.js';
import { OrgUsers } from '../components/OrgUsers.jsx';
import { RulesDialog } from '../components/RulesDialog.jsx';
import { ApiError, admin } from '../lib/api.js';
import { deriveCode } from '../lib/slug.js';
import type { AdminClient, AdminClientsResult, ClientInput, ResolveUserResult } from '../lib/types.js';
import './AdminPage.css';

const emptyForm: ClientInput = {
  code: '',
  displayName: '',
  spRootPath: '',
  atlantePrefix: '',
  templateId: null,
};

export function AdminPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AdminClient | 'new' | null>(null);
  const [showUsers, setShowUsers] = useState<AdminClient | null>(null);
  const [showRules, setShowRules] = useState<AdminClient | null>(null);

  const data = useQuery<AdminClientsResult>({
    queryKey: ['admin', 'clients'],
    queryFn: () => admin.clients(),
  });

  const deactivate = useMutation({
    mutationFn: (id: number) => admin.deactivateClient(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] }),
  });

  const reactivate = useMutation({
    mutationFn: (id: number) => admin.updateClient(id, { isActive: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] }),
  });

  return (
    <div className="admin">
      <div className="admin__head">
        <div>
          <h1>Organizzazioni</h1>
          <p className="admin__lead">
            Ogni utenza AtlantePlus inizia con 4 lettere che identificano l'organizzazione
            (es. <code>FRIG</code>). Mappa il prefisso alla cartella condivisa: l'utente
            viene abilitato al primo accesso, senza altre operazioni.
          </p>
        </div>
        <button type="button" className="btn" onClick={() => setEditing('new')}>
          Nuova organizzazione
        </button>
      </div>

      <UserCheck />

      <div className="card admin__panel">
        {data.isPending ? (
          <p className="admin__state">Caricamento…</p>
        ) : data.isError ? (
          <div className="admin__state">
            <div className="alert alert--error">
              {data.error instanceof ApiError ? data.error.message : 'Caricamento non riuscito.'}
            </div>
          </div>
        ) : (
          <div className="admin__scroll">
            <table className="admin__table">
              <thead>
                <tr>
                  <th>Prefisso</th>
                  <th>Organizzazione</th>
                  <th>Cartella condivisa</th>
                  <th>Template</th>
                  <th className="admin__num">Utenti</th>
                  <th>Stato</th>
                  <th><span className="visually-hidden">Azioni</span></th>
                </tr>
              </thead>
              <tbody>
                {data.data.clients.map((c) => (
                  <tr key={c.id} className={c.isActive ? undefined : 'is-inactive'}>
                    <td>
                      {c.atlantePrefix ? (
                        <code className="admin__prefix">{c.atlantePrefix}</code>
                      ) : (
                        <span className="admin__nomap" title="Nessuna mappatura automatica">
                          non mappato
                        </span>
                      )}
                    </td>
                    <td>
                      <strong>{c.displayName}</strong>
                      <span className="admin__code">{c.code}</span>
                    </td>
                    <td>
                      <code className="admin__path" title={c.spRootPath}>
                        {c.spRootPath}
                      </code>
                    </td>
                    <td className="admin__muted">
                      {data.data.templates.find((t) => t.id === c.templateId)?.name ?? '—'}
                    </td>
                    <td className="admin__num">
                      {/*
                        Il numero da solo non si capiva che si potesse premere:
                        sembrava una colonna di conteggi. Pastiglia con la parola
                        accanto — e da qui si arriva anche alle regole per utente.
                      */}
                      <button
                        type="button"
                        className="admin__count"
                        onClick={() => setShowUsers(c)}
                        title={`Mostra gli utenti di ${c.displayName}`}
                        aria-label={`Mostra gli utenti di ${c.displayName}`}
                      >
                        <span className="admin__count-n">{c.utentiAssociati}</span>
                        <span className="admin__count-label">
                          utent{c.utentiAssociati === 1 ? 'e' : 'i'}
                        </span>
                      </button>
                    </td>
                    <td>
                      {c.isActive ? (
                        <span className="admin__badge admin__badge--on">attivo</span>
                      ) : (
                        <span className="admin__badge">disattivato</span>
                      )}
                    </td>
                    <td className="admin__actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setEditing(c)}
                      >
                        Modifica
                      </button>
                      {/*
                        Le deroghe di questa organizzazione sul template. Da qui
                        e non dalla sezione Template, perche' li' si cambia
                        quello che vale per tutti.
                      */}
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setShowRules(c)}
                      >
                        Regole
                      </button>
                      {c.isActive ? (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => deactivate.mutate(c.id)}
                        >
                          Disattiva
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => reactivate.mutate(c.id)}
                        >
                          Riattiva
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showUsers ? (
        <OrgUsers clientId={showUsers.id} onClose={() => setShowUsers(null)} />
      ) : null}

      {showRules ? (
        <RulesDialog
          target={{ scope: 'client', clientId: showRules.id, label: showRules.displayName }}
          onClose={() => setShowRules(null)}
        />
      ) : null}

      {editing ? (
        <ClientForm
          client={editing === 'new' ? null : editing}
          templates={data.data?.templates ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] });
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Verifica di una singola utenza: dice dove finirebbe, prima che l'utente
 * chiami per dire che non vede niente.
 */
function UserCheck() {
  const [username, setUsername] = useState('');
  const [result, setResult] = useState<ResolveUserResult | null>(null);

  const check = useMutation({
    mutationFn: () => admin.resolveUser(username.trim()),
    onSuccess: setResult,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (username.trim()) check.mutate();
  };

  const tone =
    result === null ? '' : result.esito === 'ok' ? 'is-ok' : 'is-warn';

  return (
    <form className="card admin__check" onSubmit={submit}>
      <label className="admin__check-field">
        <span>Verifica un'utenza AtlantePlus</span>
        <input
          className="input"
          type="text"
          value={username}
          placeholder="es. FRIG0012"
          onChange={(e) => setUsername(e.target.value)}
          autoCapitalize="characters"
          spellCheck={false}
        />
      </label>
      <button className="btn btn--ghost" type="submit" disabled={check.isPending || !username.trim()}>
        {check.isPending ? 'Verifica…' : 'Verifica'}
      </button>

      {result ? (
        <p className={`admin__check-result ${tone}`}>
          {result.prefix ? (
            <>
              Prefisso <code>{result.prefix}</code> — {result.messaggio}
            </>
          ) : (
            result.messaggio
          )}
        </p>
      ) : null}
    </form>
  );
}

function ClientForm({
  client,
  templates,
  onClose,
  onSaved,
}: {
  client: AdminClient | null;
  templates: { id: number; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<ClientInput>(
    client
      ? {
          code: client.code,
          displayName: client.displayName,
          spRootPath: client.spRootPath,
          atlantePrefix: client.atlantePrefix ?? '',
          templateId: client.templateId,
        }
      : { ...emptyForm, templateId: templates[0]?.id ?? null },
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  // Una volta che l'admin scrive il codice a mano, non glielo si sovrascrive più.
  const [codeTouched, setCodeTouched] = useState(client !== null);

  const save = useMutation({
    mutationFn: () => {
      const payload: ClientInput = {
        ...form,
        code: form.code.trim().toUpperCase(),
        displayName: form.displayName.trim(),
        // Stringa vuota significa "nessuna mappatura", non prefisso vuoto.
        atlantePrefix: form.atlantePrefix?.trim() ? form.atlantePrefix.trim().toUpperCase() : null,
      };
      return client ? admin.updateClient(client.id, payload) : admin.createClient(payload);
    },
    onSuccess: onSaved,
  });

  const set = <K extends keyof ClientInput>(key: K, value: ClientInput[K]) =>
    setForm((f) => {
      const next = { ...f, [key]: value };
      // Il codice si deriva da prefisso e nome finché l'admin non lo modifica.
      if (!codeTouched && (key === 'atlantePrefix' || key === 'displayName')) {
        next.code = deriveCode(next.atlantePrefix, next.displayName);
      }
      return next;
    });

  const prefixValue = form.atlantePrefix ?? '';
  const prefixValid = prefixValue === '' || /^[A-Za-z]{4}$/.test(prefixValue.trim());
  const canSave =
    form.code.trim() !== '' && form.displayName.trim() !== '' && form.spRootPath !== '' && prefixValid;

  return (
    <div className="picker" role="dialog" aria-modal="true">
      <form
        className="picker__box card admin__form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSave) save.mutate();
        }}
      >
        <header className="picker__head">
          <h3>{client ? `Modifica ${client.displayName}` : 'Nuova organizzazione'}</h3>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
            Chiudi
          </button>
        </header>

        <div className="admin__form-body">
          {save.error ? (
            <div className="alert alert--error">
              {save.error instanceof ApiError ? save.error.message : 'Salvataggio non riuscito.'}
            </div>
          ) : null}

          <label className="admin__field">
            <span>Prefisso AtlantePlus</span>
            <input
              className="input admin__field-prefix"
              type="text"
              value={prefixValue}
              maxLength={4}
              placeholder="FRIG"
              onChange={(e) => set('atlantePrefix', e.target.value.toUpperCase())}
              spellCheck={false}
            />
            <small>
              {prefixValid
                ? 'Le prime 4 lettere dello username. Vuoto = nessuna mappatura automatica.'
                : 'Devono essere esattamente 4 lettere.'}
            </small>
          </label>

          <label className="admin__field">
            <span>Nome organizzazione</span>
            <input
              className="input"
              type="text"
              value={form.displayName}
              onChange={(e) => set('displayName', e.target.value)}
            />
            <small>È il nome che il cliente vede nel portale.</small>
          </label>

          <label className="admin__field">
            <span>Codice interno</span>
            <input
              className="input"
              type="text"
              value={form.code}
              onChange={(e) => {
                setCodeTouched(true);
                set('code', e.target.value.toUpperCase());
              }}
              spellCheck={false}
            />
            <small>
              {codeTouched
                ? 'Maniglia stabile per script e query. Non cambiarla dopo la creazione.'
                : 'Compilato automaticamente da prefisso e nome. Modificabile.'}
            </small>
          </label>

          <div className="admin__field">
            <span>Cartella condivisa su SharePoint</span>
            <div className="admin__folder">
              <code className="admin__folder-value">{form.spRootPath || 'nessuna cartella scelta'}</code>
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setPickerOpen(true)}>
                Scegli…
              </button>
            </div>
            <small>Il percorso viene verificato su SharePoint al salvataggio.</small>
          </div>

          <label className="admin__field">
            <span>Template di permessi</span>
            <select
              className="input"
              value={form.templateId ?? ''}
              onChange={(e) => set('templateId', e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Nessuno (accesso negato)</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <footer className="picker__foot">
          <span className="admin__muted">
            {client ? 'Le modifiche sono registrate nel log di audit.' : ''}
          </span>
          <button className="btn" type="submit" disabled={!canSave || save.isPending}>
            {save.isPending ? 'Salvataggio…' : 'Salva'}
          </button>
        </footer>

        {pickerOpen ? (
          <FolderPicker
            initialPath={form.spRootPath}
            onClose={() => setPickerOpen(false)}
            onPick={(path) => {
              set('spRootPath', path);
              setPickerOpen(false);
            }}
          />
        ) : null}
      </form>
    </div>
  );
}
